import { Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { searchPractitionersByRoles } from '@/hooks/usePractitionersByRoles';
import { buildProviderDirectory, EMPTY_PROVIDER_DIRECTORY, ProviderDirectory } from '@/services/provider-directory';
import { ProviderDirectoryContext } from './contexts';

/**
 * Loads the provider roster once per signed-in user, so every teammate avatar
 * in Orbit can wear its role ring — and the Task log / task picker can list
 * staff — without their own PractitionerRole reads.
 *
 * Keyed on the profile, not the client: the MedplumClient lives for the page,
 * so an effect keyed on it would fire once (possibly while signed out) and
 * never again after a client-side sign-in or a user switch.
 *
 * A failed load leaves the directory empty: avatars fall back to the plain
 * Warm White ring, which is what they showed before roles had colors.
 */
export function ProviderDirectoryProvider({ children }: { children: ReactNode }) {
  const medplum = useMedplum();
  const profileId = useMedplumProfile()?.id;
  const [directory, setDirectory] = useState<ProviderDirectory>(EMPTY_PROVIDER_DIRECTORY);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profileId) {
      setDirectory(EMPTY_PROVIDER_DIRECTORY);
      setPractitioners([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const result = await searchPractitionersByRoles(medplum);
        if (!cancelled) {
          setDirectory(buildProviderDirectory(result.practitionerRoles, result.practitioners));
          setPractitioners(result.practitioners);
        }
      } catch {
        if (!cancelled) {
          setDirectory(EMPTY_PROVIDER_DIRECTORY);
          setPractitioners([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum, profileId]);

  const value = useMemo(() => ({ directory, practitioners, loading }), [directory, practitioners, loading]);
  return <ProviderDirectoryContext.Provider value={value}>{children}</ProviderDirectoryContext.Provider>;
}
